import React, { useState, useRef, useEffect } from "react";
import {
  X,
  Save,
  Trash2,
  Upload,
  FileText,
  Download,
  Loader,
  Loader2,
  AlertTriangle,
  CheckCircle,
  User,
  Mail,
  Phone,
  Calendar,
  Tag,
  Building,
  Clock,
  Edit3,
  FileTextIcon,
} from "lucide-react";
import { apiClient } from "../../../config/api";
import API_CONFIG from "../../../config/api";
import CustomerRequestChatbot from "./CustomerRequestChatbot";
import { useUser } from "@clerk/clerk-react";

// Helper to get Clerk token for authenticated requests
const getAuthToken = async () => {
  try {
    const clerk = window.Clerk;
    if (clerk?.session) {
      return await clerk.session.getToken();
    }
  } catch (error) {
    console.warn("Failed to get Clerk token:", error);
  }
  return null;
};

// Helper function to format date for display
const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

export default function CustomerRequestDetailView({
  request,
  onClose,
  onUpdate,
  onDelete,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const fileInputRef = useRef(null);
  const { user } = useUser();
  const userClient = user?.publicMetadata?.client;
  // Form state
  const [formData, setFormData] = useState({
    description: request?.description || "",
    severity: request?.priority || "Medium",
    status: request?.status || "Open",
    email: request?.email || "",
    phoneNumber: request?.phone || "",
    assignPerson: request?.assignee || "",
    tags: request?.tags || "",
  });

  // Load attachments
  useEffect(() => {
    if (request?.id) {
      loadAttachments();
    }
  }, [request?.id]);

  // Reset form data when request changes
  useEffect(() => {
    setFormData({
      description: request?.description || "",
      severity: request?.priority || "Medium",
      status: request?.status || "Open",
      email: request?.email || "",
      phoneNumber: request?.phone || "",
      assignPerson: request?.assignee || "",
      tags: request?.tags || "",
    });
  }, [request]);

  const getNumericId = () => {
    if (!request?.id) return null;
    const idStr = request.id.toString();
    const match = idStr.match(/(\d+)$/);
    return match ? match[1] : idStr;
  };

  const loadAttachments = async () => {
    const numericId = getNumericId();
    if (!numericId) return;

    setLoadingAttachments(true);
    try {
      const response = await apiClient.get(
        API_CONFIG.ENDPOINTS.CUSTOMER_REQUEST_ATTACHMENTS(numericId)
      );
      setAttachments(response.data || []);
    } catch (err) {
      console.error("Failed to load attachments:", err);
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    const numericId = getNumericId();
    if (!numericId) return;

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.put(
        API_CONFIG.ENDPOINTS.CUSTOMER_REQUEST(numericId),
        {
          description: formData.description,
          severity: formData.severity,
          status: formData.status,
          companyName: userClient?.name,
          createdBy: user?.fullName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          assignPerson: formData.assignPerson,
          tags: formData.tags,
        }
      );

      setSuccess("Request updated successfully!");
      setIsEditing(false);

      if (onUpdate) {
        onUpdate(response.data);
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Failed to update request:", err);
      setError(err.response?.data?.message || "Failed to update request");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    setFormData({
      description: request?.description || "",
      severity: request?.priority || "Medium",
      status: request?.status || "Open",
      email: request?.email || "",
      phoneNumber: request?.phone || "",
      assignPerson: request?.assignee || "",
      tags: request?.tags || "",
    });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    const numericId = getNumericId();
    if (!numericId) return;

    setIsDeleting(true);
    setError(null);

    try {
      await apiClient.delete(API_CONFIG.ENDPOINTS.CUSTOMER_REQUEST(numericId));

      if (onDelete) {
        onDelete(request);
      }
      onClose();
    } catch (err) {
      console.error("Failed to delete request:", err);
      setError(err.response?.data?.message || "Failed to delete request");
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const numericId = getNumericId();
    if (!numericId) return;

    setIsUploading(true);
    setError(null);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);

      await apiClient.post(
        API_CONFIG.ENDPOINTS.CUSTOMER_REQUEST_UPLOAD(numericId),
        uploadFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setSuccess("File uploaded successfully!");
      loadAttachments();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Failed to upload file:", err);
      setError(err.response?.data?.message || "Failed to upload file");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Download attachment using the new API with Bearer token
  const handleDownloadAttachment = async (attachment) => {
    const { attachmentId, fileName } = attachment;

    if (!attachmentId) {
      setError("Attachment ID is not available for download.");
      return;
    }

    setDownloadingAttachmentId(attachmentId);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error("Authentication required. Please sign in.");
      }

      const downloadUrl = `${
        API_CONFIG.BASE_URL
      }${API_CONFIG.ENDPOINTS.CUSTOMER_REQUEST_ATTACHMENT_DOWNLOAD(
        attachmentId
      )}`;

      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText || `Download failed with status ${response.status}`
        );
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "attachment";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error("Error downloading file:", err);
      setError(err.message || "Failed to download file");
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  // Soft delete attachment using the new API
  const handleDeleteAttachment = async (attachment) => {
    const { attachmentId, fileName } = attachment;

    if (!attachmentId) {
      setError("Attachment ID is not available for deletion.");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
      return;
    }

    setDeletingAttachmentId(attachmentId);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error("Authentication required. Please sign in.");
      }

      const deleteUrl = `${
        API_CONFIG.BASE_URL
      }${API_CONFIG.ENDPOINTS.CUSTOMER_REQUEST_ATTACHMENT_DELETE(
        attachmentId
      )}`;

      const response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 204 || response.ok) {
        // Remove from local state
        setAttachments((prev) =>
          prev.filter((att) => att.attachmentId !== attachmentId)
        );
        setSuccess("Attachment deleted successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorText = await response.text();
        throw new Error(
          errorText || `Delete failed with status ${response.status}`
        );
      }
    } catch (err) {
      console.error("Error deleting attachment:", err);
      setError(err.message || "Failed to delete attachment");
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case "Critical":
        return "badge badge--critical";
      case "High":
        return "badge badge--high";
      case "Medium":
        return "badge badge--medium";
      case "Low":
        return "badge badge--low";
      default:
        return "badge";
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "Open":
      case "New":
        return "chip chip--info";
      case "In Progress":
        return "chip chip--warn";
      case "Escalated":
        return "chip chip--danger";
      case "Completed":
      case "Resolved":
        return "chip chip--success";
      case "On Hold":
      case "Closed":
        return "chip chip--muted";
      default:
        return "chip";
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return "📄";
      case "doc":
      case "docx":
        return "📝";
      case "xls":
      case "xlsx":
        return "📊";
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
        return "🖼️";
      case "zip":
      case "rar":
        return "📦";
      default:
        return "📎";
    }
  };

  // Render editable or view field
  const renderField = (label, icon, viewValue, editElement) => (
    <div className="request-view__item">
      <span className="request-view__label">
        {icon}
        {label}
      </span>
      {isEditing ? (
        <div className="request-view__edit-field">{editElement}</div>
      ) : (
        <span className="request-view__value">{viewValue}</span>
      )}
    </div>
  );

  return (
    <div className="request-detail-overlay">
      <div className="request-detail-modal">
        {/* Header */}
        <div className="request-detail__header">
          <div className="request-detail__title-section">
            <div className="request-detail__id-section">
              <span className="request-detail__id">{request?.id}</span>
              <span className={getSeverityClass(request?.priority)}>
                {request?.priority}
              </span>
              <h2 className="request-detail__title">{request?.title}</h2>
            </div>
          </div>
          <div className="request-detail__header-actions">
            {isEditing ? (
              <>
                <button
                  className="btn btn--outline btn--sm"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn--primary btn--sm"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader size={14} className="spinning" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={14} />
                      Save
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn--outline btn--sm"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit3 size={14} />
                  Edit
                </button>
                {userClient?.name === "Helxon" && (
                  <button
                    className="btn btn--danger btn--sm"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </>
            )}
            <button className="request-detail__close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="request-detail__message request-detail__message--error">
            <AlertTriangle size={16} />
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
        {success && (
          <div className="request-detail__message request-detail__message--success">
            <CheckCircle size={16} />
            <span>{success}</span>
          </div>
        )}

        {/* Content Wrapper */}
        <div className="request-detail__content-wrapper">
          {/* Main Content */}
          <div className="request-detail__content">
            <div className="request-view">
              {/* Request Info Section */}
              <div className="request-view__section">
                <h3 className="request-view__section-title">
                  Request Information
                </h3>

                {/* Description - Full Width */}
                <div className="request-view__item request-view__item--full">
                  <span className="request-view__label">
                    <FileText size={14} />
                    Description
                  </span>
                  {isEditing ? (
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      className="request-view__input request-view__input--textarea"
                      placeholder="Describe the request..."
                    />
                  ) : (
                    <span className="request-view__value request-view__value--description">
                      {request?.description || "-"}
                    </span>
                  )}
                </div>

                <div className="request-view__grid">
                  {/* Customer - Read Only */}
                  <div className="request-view__item">
                    <span className="request-view__label">
                      <Building size={14} />
                      Customer
                    </span>
                    <span className="request-view__value">
                      {request?.customer || "-"}
                    </span>
                  </div>

                  {/* Contact - Read Only */}
                  <div className="request-view__item">
                    <span className="request-view__label">
                      <User size={14} />
                      Contact
                    </span>
                    <span className="request-view__value">
                      {request?.contact || "-"}
                    </span>
                  </div>

                  {/* Email - Editable */}
                  {renderField(
                    "Email",
                    <Mail size={14} />,
                    request?.email || "-",
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="request-view__input"
                      placeholder="user@example.com"
                    />
                  )}

                  {/* Phone - Editable */}
                  {renderField(
                    "Phone",
                    <Phone size={14} />,
                    request?.phone || "-",
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="request-view__input"
                      placeholder="+1234567890"
                    />
                  )}

                  {/* Created - Read Only */}
                  <div className="request-view__item">
                    <span className="request-view__label">
                      <Calendar size={14} />
                      Created
                    </span>
                    <span className="request-view__value">
                      {formatDateTime(request?.created)}
                    </span>
                  </div>

                  {/* Last Response - Read Only */}
                  <div className="request-view__item">
                    <span className="request-view__label">
                      <Clock size={14} />
                      Last Response
                    </span>
                    <span className="request-view__value">
                      {formatDateTime(request?.lastResponse)}
                    </span>
                  </div>

                  {/* Assignee - Editable */}
                  {renderField(
                    "Assignee",
                    <User size={14} />,
                    request?.assignee || "Unassigned",
                    <input
                      type="text"
                      name="assignPerson"
                      value={formData.assignPerson}
                      onChange={handleInputChange}
                      className="request-view__input"
                      placeholder="Assignee name"
                    />
                  )}

                  {/* Tags - Editable */}
                  {renderField(
                    "Tags",
                    <Tag size={14} />,
                    request?.tags || "-",
                    <input
                      type="text"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      className="request-view__input"
                      placeholder="vpn, network, security"
                    />
                  )}

                  {/* Priority/Severity - Editable */}
                  <div className="request-view__item">
                    <span className="request-view__label">Priority</span>
                    {isEditing ? (
                      <select
                        name="severity"
                        value={formData.severity}
                        onChange={handleInputChange}
                        className="request-view__select"
                      >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Critical">Critical</option>
                      </select>
                    ) : (
                      <span className={getSeverityClass(request?.priority)}>
                        {request?.priority}
                      </span>
                    )}
                  </div>

                  {/* Status - Editable */}
                  <div className="request-view__item">
                    <span className="request-view__label">Status</span>
                    {isEditing ? (
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="request-view__select"
                      >
                        <option value="New">New</option>
                        <option value="Open">Open</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Escalated">Escalated</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Closed">Closed</option>
                      </select>
                    ) : (
                      <span className={getStatusClass(request?.status)}>
                        {request?.status}
                      </span>
                    )}
                  </div>

                  {/* Category - Read Only */}
                  <div className="request-view__item">
                    <span className="request-view__label">Category</span>
                    <span className="chip chip--purple">
                      {request?.category}
                    </span>
                  </div>
                </div>
              </div>

              {/* Attachments Section */}
              <div className="request-view__section">
                <div className="request-view__section-header">
                  <h3 className="request-view__section-title">Attachments</h3>
                  <div className="request-view__section-actions">
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      style={{ display: "none" }}
                    />
                    <button
                      className="btn btn--outline btn--sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <>
                          <Loader size={14} className="spinning" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload size={14} />
                          Upload File
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {loadingAttachments ? (
                  <div className="request-view__loading">
                    <Loader size={20} className="spinning" />
                    <span>Loading attachments...</span>
                  </div>
                ) : attachments.length === 0 ? (
                  <div className="request-view__empty">
                    <FileText size={32} className="request-view__empty-icon" />
                    <p>No attachments yet</p>
                  </div>
                ) : (
                  <div className="attachments-list">
                    {attachments.map((attachment) => (
                      <div
                        key={attachment.attachmentId}
                        className="attachment-card"
                      >
                        <span className="attachment-card__icon">
                          {getFileIcon(attachment.fileName)}
                        </span>
                        <div className="attachment-card__info">
                          <span className="attachment-card__name">
                            {attachment.fileName}
                          </span>
                          <span className="attachment-card__meta">
                            {attachment.fileSize &&
                              `${(attachment.fileSize / 1024).toFixed(1)} KB`}
                          </span>
                        </div>
                        <div className="attachment-card__actions">
                          <button
                            className="btn btn--icon"
                            onClick={() => handleDownloadAttachment(attachment)}
                            disabled={
                              downloadingAttachmentId ===
                              attachment.attachmentId
                            }
                            title="Download"
                          >
                            {downloadingAttachmentId ===
                            attachment.attachmentId ? (
                              <Loader2 size={14} className="spinning" />
                            ) : (
                              <Download size={14} />
                            )}
                          </button>
                          <button
                            className="btn btn--icon btn--danger"
                            onClick={() => handleDeleteAttachment(attachment)}
                            disabled={
                              deletingAttachmentId === attachment.attachmentId
                            }
                            title="Delete"
                          >
                            {deletingAttachmentId ===
                            attachment.attachmentId ? (
                              <Loader2 size={14} className="spinning" />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Chat Sidebar */}
          <div className="request-detail__sidebar">
            <CustomerRequestChatbot request={request} />
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="confirm-modal">
            <div
              className="confirm-modal__overlay"
              onClick={() => setShowDeleteConfirm(false)}
            />
            <div className="confirm-modal__content">
              <h3>Delete Request</h3>
              <p>
                Are you sure you want to delete this request? This action cannot
                be undone.
              </p>
              <div className="confirm-modal__actions">
                <button
                  className="btn btn--outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn--danger"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Loader size={16} className="spinning" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
